import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Form, Button, Spinner, OverlayTrigger, Tooltip, Alert, Badge, InputGroup } from "react-bootstrap";
import RegistrasiService from "../../services/registrasi/RegistrasiService";
import Card from "../../components/Card";
import ModalUPT from "../../views/registrasi/modal/ModalUPT";
import ActivatePassphrase from "../../views/dashboard/Passphrase/activatePassphrase";

const EditPegawai = () => {
    const nip = localStorage.getItem('username');
    
    const [formData, setFormData] = useState({
        NIP: "",
        NAMA: "",
        JABATAN: "",
        UNIT: "",
        KD_UNIT: "",
        STATUS: "",
        NO_REG: "",
        TELEPON: "",
        PASSPHRASE: null,
        EXPIRED_DATE: null
    });

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showPassphraseModal, setShowPassphraseModal] = useState(false);
    
    const RegService = useMemo(() => new RegistrasiService(), []);

    // 2. State untuk Modal Pencarian UPT
    const [showUPT, setShowUPT] = useState(false);
    const handleCloseUPT = () => setShowUPT(false);

    const setKodeUPT = (kode) => setFormData((prev) => ({ ...prev, KD_UNIT: kode }));
    const setNamaUPT = (nama) => setFormData((prev) => ({ ...prev, UNIT: nama }));

    // 3. Fetch Data Pegawai
    useEffect(() => {
    const fetchPegawai = async () => {
        try {
            const res = await RegService.getPegawai(nip);
            const { data } = res;

            const passData = data.Passpharase;
            const isPassphraseActive = Boolean(passData && passData.expired_date);

            const formattedExpiredDate = passData?.expired_date
                ? new Date(passData.expired_date).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                  })
                : null;

            setFormData({
                NIP: data.NIP || nip || "",
                NAMA: data.NAMA || "",
                JABATAN: data.JABATAN || "",
                UNIT: data.UNIT || "",
                KD_UNIT: data.KD_UNIT || "",
                STATUS: data.STATUS || "",
                TELEPON: data.TELEPON || "",
                NO_REG: data.NO_REG || "-",
                IS_PASSPHRASE_ACTIVE: isPassphraseActive,
                EXPIRED_DATE: formattedExpiredDate
            });
        } catch (error) {
            setErrorMessage("Gagal mengambil data pegawai. Silakan muat ulang halaman.");
        } finally {
            setLoading(false);
        }
    };

    if (nip) fetchPegawai();
}, [nip, RegService]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage("");
        setErrorMessage("");
        setSubmitting(true);

        try {
            await RegService.updatePegawai(nip, {
                NAMA: formData.NAMA,
                JABATAN: formData.JABATAN,
                UNIT: formData.UNIT,
                KD_UNIT: formData.KD_UNIT,
                STATUS: formData.STATUS,
                TELEPON: formData.TELEPON,
                NO_REG: formData.NO_REG
            });
            setSuccessMessage("Data profil berhasil diperbarui!");
        } catch (error) {
            setErrorMessage(error.message || "Terjadi kesalahan saat memperbarui data.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center flex-column my-5" style={{ minHeight: "300px" }}>
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted fw-medium">Menyiapkan profil Anda...</p>
            </div>
        );
    }

    return (
        <div className="edit-pegawai-container">
            {/* Header Profil */}
            <Row className="mb-4">
                <Col md={12}>
                    <div className="p-4 rounded text-white shadow-sm d-flex flex-column justify-content-center">
                        <span className="text-uppercase tracking-wider fs-6 text-white-50 fw-bold mb-1">Pengaturan Akun</span>
                        <h4 className="text-white fw-bolder mb-0">EDIT PROFIL</h4>
                        <h2 className="text-white mt-1 fw-bold">{formData.NAMA || "Nama Pegawai"}</h2>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white border-bottom py-3 d-flex align-items-center">
                            <h5 className="card-title m-0 text-primary fw-bold">Informasi Data & Keamanan</h5>
                        </Card.Header>

                        <Card.Body className="p-4">
                            {successMessage && <Alert variant="success" dismissible onClose={() => setSuccessMessage("")}>{successMessage}</Alert>}
                            {errorMessage && <Alert variant="danger" dismissible onClose={() => setErrorMessage("")}>{errorMessage}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">
                                    {/* Bagian Kiri: Form Data Diri */}
                                    <Col lg={7} md={12}>
                                        <div className="p-4 border rounded-3 bg-light h-100">
                                            <h6 className="fw-bold mb-4 text-secondary border-bottom pb-2">Detail Pegawai</h6>
                                            
                                            <Form.Group controlId="nip" className="mb-3">
                                                <Form.Label className="fw-semibold text-dark">
                                                    NIP 
                                                    <OverlayTrigger overlay={<Tooltip>Nomor Induk Pegawai bersifat tetap.</Tooltip>}>
                                                        <span className="ms-1 text-danger cursor-pointer">*</span>
                                                    </OverlayTrigger>
                                                </Form.Label>
                                                <Form.Control type="text" value={formData.NIP} disabled className="bg-white text-muted" />
                                            </Form.Group>

                                            <Form.Group controlId="nama" className="mb-3">
                                                <Form.Label className="fw-semibold text-dark">Nama Lengkap</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="NAMA"
                                                    value={formData.NAMA}
                                                    onChange={handleChange}
                                                    placeholder="Masukkan nama lengkap"
                                                    required
                                                />
                                            </Form.Group>

                                            <Form.Group controlId="jabatan" className="mb-3">
                                                <Form.Label className="fw-semibold text-dark">Jabatan</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="JABATAN"
                                                    value={formData.JABATAN}
                                                    onChange={handleChange}
                                                    placeholder="Contoh: Analis Sistem"
                                                    required
                                                />
                                            </Form.Group>

                                            <Form.Group controlId="unit" className="mb-3">
                                                <Form.Label className="fw-semibold text-dark">Unit Kerja / UPT
                                                    <OverlayTrigger overlay={<Tooltip>Hanya dapat diubah oleh admin pusat</Tooltip>}>
                                                        <span className="ms-1 text-danger cursor-pointer">*</span>
                                                    </OverlayTrigger>
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.UNIT}
                                                        disabled
                                                        placeholder="Pilih unit kerja..."
                                                        className="bg-white"
                                                    />
                                                    {/* <Button variant="secondary" onClick={() => setShowUPT(true)}>
                                                        UPT
                                                    </Button> */}
                                                </InputGroup>
                                            </Form.Group>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group controlId="telepon" className="mb-3">
                                                        <Form.Label className="fw-semibold text-dark">Nomor Telepon</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="TELEPON"
                                                            value={formData.TELEPON}
                                                            onChange={handleChange}
                                                            placeholder="08123456789"
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group controlId="noReg" className="mb-3">
                                                        <Form.Label className="fw-semibold text-dark">No. Registrasi</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="NO_REG"
                                                            value={formData.NO_REG}
                                                            onChange={handleChange}
                                                            placeholder="Nomor registrasi (opsional)"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>

                                    {/* Bagian Kanan: Status Passphrase */}
                                    <Col lg={5} md={12}>
                                        <div className="p-4 border rounded-3 bg-light h-100 d-flex flex-column">
                                            <h6 className="fw-bold mb-4 text-secondary border-bottom pb-2">Status Passphrase</h6>
                                           
                                            <div className="flex-grow-1">
                                                    {formData.IS_PASSPHRASE_ACTIVE ? (
                                                        <div className="p-4 border rounded-3 bg-white shadow-sm text-center">
                                                            <div className="mb-3">
                                                                <div className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: "50px", height: "50px" }}>
                                                                    <i className="bi bi-shield-check fs-3"></i>
                                                                </div>
                                                                <h6 className="fw-bold text-dark m-0">Passphrase Terkonfigurasi</h6>
                                                            </div>
                                                            <Badge bg="success" className="px-3 py-2 mb-3 fs-6 rounded-pill">Status: Aktif</Badge>
                                                            <div className="bg-light p-2 rounded text-muted small">
                                                                <span className="d-block mb-1">Masa Berlaku S.D:</span>
                                                                <strong className="text-dark fs-6">{formData.EXPIRED_DATE || " "}</strong>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="p-4 border rounded-3 bg-white shadow-sm text-center">
                                                            <div className="mb-0">
                                                                <div className="text-danger mb-0" style={{ fontSize: "5rem" }}>⚠️</div>
                                                            </div>
                                                            <Badge bg="warning" text="dark" className="px-3 py-2 mb-3 fs-6 rounded-pill">Status: Belum Aktif</Badge>
                                                            <p className="text-muted small mb-4 px-2">Anda belum mengaktifkan Passphrase untuk keperluan sertifikat digital dan tanda tangan elektronik.</p>
                                                            <Button 
                                                                variant="primary" 
                                                                className="w-100 fw-bold shadow-sm"
                                                                onClick={() => setShowPassphraseModal(true)}
                                                            >
                                                                Aktifkan Passphrase Sekarang
                                                            </Button>
                                                        </div>
                                                    )}
                                            </div>

                                            <div className="alert alert-info border-0 mt-4 mb-0 small">
                                                <i className="bi bi-info-circle-fill me-2"></i>
                                                <strong>Catatan:</strong> Pastikan seluruh data diri Anda valid dan Passphrase tetap aktif untuk kemudahan pengesahan dokumen.
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <hr className="my-4 text-muted" />
                                <div className="d-flex justify-content-end gap-2">
                                    {/* <Button variant="light" type="button" disabled={submitting} className="px-4 border">
                                        Batal
                                    </Button> */}
                                    <Button variant="primary" type="submit" disabled={submitting} className="px-4 fw-semibold shadow-sm">
                                        {submitting ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                Menyimpan Data...
                                            </>
                                        ) : (
                                            "Simpan Perubahan"
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modals */}
            <ModalUPT
                showUPT={showUPT}
                handleCloseUPT={handleCloseUPT}
                setKodeUPT={setKodeUPT}
                setNamaUPT={setNamaUPT}
            />

            <ActivatePassphrase
                show={showPassphraseModal}
                handleClose={() => setShowPassphraseModal(false)}
            />
        </div>
    );
};

export default EditPegawai;